import { useState, useRef, useEffect, useCallback } from "react";
import { axiosInstance } from "../../config";
import "./AgencyOnboarding.css";

/**
 * AgencyOnboardingPage — Typeform-style agency client intake. One question per
 * screen, keyboard-driven (Enter advances, Shift+Enter = line break, Cmd/Ctrl+
 * Enter submits on the last step). Answers post to /api/agency-onboarding.
 */

// Each step drives one screen. `key` maps to the payload field.
const STEPS = [
  {
    id: "intro", type: "welcome",
    title: "Welcome! We’re excited to get started",
    subtitle: "This short onboarding form will help us understand your goals, assets, and preferences so we can launch your distribution the right way.",
  },
  {
    id: "goal", type: "longtext", key: "goal", n: 1, required: true,
    title: "Please describe your primary goal with distribution to the best of your ability",
  },
  {
    id: "promote", type: "longtext", key: "promote", n: 2, required: true,
    title: "Is there anything specific you want pushed or promoted right now?",
    hint: "(ex: new video, new product, community, movie, album, etc.) — if so, drop links below",
  },
  {
    id: "trafficPages", type: "longtext", key: "trafficPages", n: 3, required: true,
    title: "Which main pages should we direct traffic to?",
    hint: "Please paste the links for your primary pages on IG, TikTok, YT",
  },
  {
    id: "contentStyles", type: "longtext", key: "contentStyles", n: 4, required: true,
    title: "What content styles do you prefer?",
    hint: "(ex. talking head clips, lifestyle edits, raw moments, no preference)",
  },
  {
    id: "styleRef", type: "longtext", key: "styleRef", n: 5, required: false,
    title: "Drop a link to an account or clips that represent the style you want the clippers to model",
  },
  {
    id: "postingTargets", type: "multi", key: "postingTargets", n: 6, required: false,
    title: "Where do you want your clips to get posted?",
    hint: "Choose as many as you like",
    options: [
      { key: "A", label: "On pages under your own brand only" },
      { key: "B", label: "On large theme pages in your niche only" },
      { key: "C", label: "Both (branded pages + theme pages)" },
    ],
  },
  {
    id: "nicheRefs", type: "longtext", key: "nicheRefs", n: 7, required: true,
    title: "Drop the social links to a couple brands/figures in your niche so we can research angles",
  },
  {
    id: "assets", type: "longtext", key: "assets", n: 8, required: false,
    title: "Drop any content assets links you currently have available for us to clip",
    hint: "(Google drive, YouTube video, Dropbox, etc.)",
  },
];

const TOTAL = STEPS.length;

export default function AgencyOnboardingPage() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1); // 1 = forward, -1 = back (drives slide anim)
  const [answers, setAnswers] = useState({
    firstName: "", lastName: "", company: "",
    goal: "", promote: "", trafficPages: "", contentStyles: "",
    styleRef: "", nicheRefs: "", assets: "",
    postingTargets: [],
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const step = STEPS[index];
  const isLast = index === TOTAL - 1;

  // Autofocus the primary field on each step change.
  useEffect(() => {
    setError("");
    const t = setTimeout(() => inputRef.current?.focus(), 260);
    return () => clearTimeout(t);
  }, [index]);

  const set = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));

  const isFilled = useCallback((s) => {
    if (s.type === "welcome") return answers.firstName.trim().length > 0;
    if (s.type === "multi") return answers[s.key].length > 0;
    return answers[s.key]?.trim().length > 0;
  }, [answers]);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await axiosInstance.post("/agency-onboarding", answers);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error === "goal_required"
        ? "Please answer the primary-goal question."
        : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [answers, submitting]);

  const next = useCallback(() => {
    if (step.required && !isFilled(step)) {
      setError("This one’s required to continue.");
      return;
    }
    if (isLast) { submit(); return; }
    setDir(1);
    setIndex((i) => Math.min(TOTAL - 1, i + 1));
  }, [step, isLast, isFilled, submit]);

  const back = useCallback(() => {
    if (index === 0) return;
    setDir(-1);
    setIndex((i) => Math.max(0, i - 1));
  }, [index]);

  // Enter advances; Shift+Enter = line break inside text; Cmd/Ctrl+Enter always advances.
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      if (e.shiftKey) return; // let the textarea insert a newline
      e.preventDefault();
      next();
    }
  };

  const toggleMulti = (optKey) => {
    setError("");
    setAnswers((a) => {
      const cur = a.postingTargets;
      return { ...a, postingTargets: cur.includes(optKey) ? cur.filter((k) => k !== optKey) : [...cur, optKey] };
    });
  };

  if (done) {
    return (
      <div className="agon">
        <div className="agon-stage">
          <div className="agon-screen agon-done">
            <div className="agon-check">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h1 className="agon-q">You’re all set{answers.firstName ? `, ${answers.firstName}` : ""} 🎉</h1>
            <p className="agon-sub">Thanks for the detail — our team will review everything and start building your distribution. We’ll be in touch shortly.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agon">
      {/* progress */}
      <div className="agon-progress"><div className="agon-progress-fill" style={{ width: `${(index / (TOTAL - 1)) * 100}%` }} /></div>

      <div className="agon-stage">
        <div key={step.id} className={`agon-screen ${dir >= 0 ? "agon-in-fwd" : "agon-in-back"}`}>
          {/* Question number */}
          {step.type !== "welcome" && (
            <div className="agon-num">{step.n}<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 12 19 12" /><polyline points="12 5 19 12 12 19" /></svg></div>
          )}

          {/* WELCOME */}
          {step.type === "welcome" && (
            <>
              <h1 className="agon-q">{step.title}</h1>
              <p className="agon-sub">{step.subtitle}</p>
              <div className="agon-namegrid">
                <input ref={inputRef} className="agon-input" placeholder="First name" value={answers.firstName}
                  onChange={(e) => set("firstName", e.target.value)} onKeyDown={onKeyDown} />
                <input className="agon-input" placeholder="Last name" value={answers.lastName}
                  onChange={(e) => set("lastName", e.target.value)} onKeyDown={onKeyDown} />
                <input className="agon-input" placeholder="Company" value={answers.company}
                  onChange={(e) => set("company", e.target.value)} onKeyDown={onKeyDown} />
              </div>
            </>
          )}

          {/* LONG TEXT */}
          {step.type === "longtext" && (
            <>
              <h1 className="agon-q">{step.title}{step.required && <span className="agon-star">*</span>}</h1>
              {step.hint && <p className="agon-hint">{step.hint}</p>}
              <textarea ref={inputRef} className="agon-textarea" rows={1} placeholder="Type your answer here…"
                value={answers[step.key]} onChange={(e) => set(step.key, e.target.value)} onKeyDown={onKeyDown}
                onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; }} />
              <p className="agon-linebreak">Shift ⇧ + Enter ↵ to make a line break</p>
            </>
          )}

          {/* MULTI SELECT */}
          {step.type === "multi" && (
            <>
              <h1 className="agon-q">{step.title}</h1>
              {step.hint && <p className="agon-hint">{step.hint}</p>}
              <div className="agon-options">
                {step.options.map((o) => {
                  const on = answers.postingTargets.includes(o.key);
                  return (
                    <button key={o.key} ref={o.key === "A" ? inputRef : null}
                      className={`agon-opt ${on ? "on" : ""}`} onClick={() => toggleMulti(o.key)}>
                      <span className="agon-opt-key">{o.key}</span>
                      <span className="agon-opt-label">{o.label}</span>
                      {on && <svg className="agon-opt-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {error && <p className="agon-error">{error}</p>}

          {/* Footer / OK */}
          <div className="agon-foot">
            <button className="agon-ok" onClick={next} disabled={submitting}>
              {submitting ? "Submitting…" : isLast ? "Submit" : "OK"}
              {!submitting && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
            </button>
            <span className="agon-enter">press {isLast ? "Cmd ⌘ + Enter ↵" : "Enter ↵"}</span>
          </div>
        </div>
      </div>

      {/* nav arrows */}
      <div className="agon-nav">
        <button className="agon-arrow" onClick={back} disabled={index === 0} aria-label="Previous">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
        </button>
        <button className="agon-arrow" onClick={next} disabled={submitting} aria-label="Next">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </div>
    </div>
  );
}
